import moment from 'moment'
import { round } from 'app/app/common/prices'
import * as i18n from 'i18next'
import { t } from 'i18next'
import {
  EditableEquipmentFields,
  EscalatorQuestion,
  ElevatorQuestion,
  EquipmentType,
  EquipmentQuestion,
  BuildingQuestion,
  ChoiceAsKeyValuePair
} from 'types/types'
import { operationalEnvironmentOptions } from '../../utils/client-constants'

// Validation Result Types (functional alternative to classes)
export type Valid<T> = {
  type: 'valid'
  value: T
}

export type ValidationError = {
  columnId: string
  reason: string
}

export type Invalid = {
  type: 'invalid'
  errors: ValidationError[]
}

export type ValidationResult<T> = Valid<T> | Invalid

// Helper functions to create validation results
export const createValid = <T>(value: T): Valid<T> => ({
  type: 'valid',
  value
})

export const createInvalid = (errors: ValidationError[]): Invalid => ({
  type: 'invalid',
  errors
})

export const createValidationError = (columnId: string, reason: string): ValidationError => ({
  columnId,
  reason
})

export interface Validator<T> {
  /* Transforms a value from an <input> element (always a string) to the domain T. */
  coerce: (value: string | boolean) => T
  /* Checks if a coerced value is a valid value in the domain. */
  validate: (value: T) => boolean
  /* Checks if coerced value is valid value in the paired domain */
  pairValidate?: (p: boolean) => (q: boolean) => boolean
  /* Returns a translated error message if validation fails, or null if valid */
  getErrorReason?: (value: T, question?: any) => string | null
}

type Validations<T> = { [K in keyof T]: Validator<T[K]> }

const string = <S extends string>(value: string | boolean): S => {
  if (typeof value === 'string') {
    return value as S
  }
  return '' as S
}

const stringOrNull = <S extends string>(value: string | boolean): S | null => {
  if (typeof value === 'string') {
    return value.length > 0 ? (value as S) : null
  }
  return null
}

// const booleanOrNull = (value: any): boolean | null => typeof value === 'boolean' ? value : null

const notNull = <T>(value: T | null): boolean => value !== null
const notEmptyString = (value: string | number | boolean | null): boolean =>
  typeof value === 'string' && value.length > 0

const isNumberBetween =
  (lower: number, upper: number) =>
  (num: string | number | boolean | null): boolean => {
    if (typeof num === 'number') {
      return Number.isFinite(num) && num >= lower && num <= upper
    }
    return false
  }

const isIntegerBetween = (lower: number, upper: number) => {
  const isBetween = isNumberBetween(lower, upper)
  return (num: string | number | boolean | null): boolean => {
    if (typeof num === 'number') {
      return Number.isInteger(num) && isBetween(num)
    }
    return false
  }
}

const numberOrNull = (value: string | boolean): number | null => {
  if (typeof value !== 'string' || value.length === 0) {
    return null
  }
  const maybeNumber = round(Number(value))
  return Number.isFinite(maybeNumber) ? maybeNumber : null
}

const alwaysValid = () => true

const startYear = 1800
const endYear = moment().year() + 5

const hasLength = (limit: number) => (value: any) => value != null && value.toString().length <= limit
const validateSpeed = (elevatorQuestion: ElevatorQuestion) => (num: string | number | boolean | null) =>
  isNumberBetween(elevatorQuestion.min ?? 0, elevatorQuestion.max ?? 20)(num) && hasLength(4)(num)

const isIncludedIn = <T>(arr: T[], value: T | null | undefined) => value != null && arr.includes(value as T)

const isNullOrEmpty = (value: string | number | boolean | null): boolean =>
  value === null || value === '' || value === undefined

const getMandatoryErrorIfNeeded = (mandatory: boolean, value: string | number | boolean | null): string | null =>
  mandatory && isNullOrEmpty(value)
    ? getErrorReasonFromValidator(notNullOrEmptyValidator, value as string | null)
    : null

const getSelectChoicesValidator = (question: BuildingQuestion | EquipmentQuestion, value: any): string | null => {
  if (question.type !== 'select' || !question.choices) {
    return t('xlAssessmentWizard.validation.invalidValue')
  }

  const isSingleType = question.choices.some((ch: string | ChoiceAsKeyValuePair) => typeof ch === 'string')
  const list = isSingleType ? (question.choices as string[]) : getListOfKeys(question.choices as ChoiceAsKeyValuePair[])
  const validator = question.mandatory ? includedInValidator(list) : includedInOrNullValidator(list)
  return getErrorReasonFromValidator(validator as any, value)
}

const createSelectFieldValidator = (
  question: EquipmentQuestion,
  validateFn: (question: EquipmentQuestion) => (value: string | number | boolean | null) => boolean
): Validator<string | number | boolean | null> => ({
  coerce: (value: string | boolean) => stringOrNull(value),
  validate: (value: string | number | boolean | null) => validateFn(question)(value),
  getErrorReason: (value: string | number | null | boolean) => {
    if (question.mandatory && isNullOrEmpty(value)) {
      return getErrorReasonFromValidator(notNullOrEmptyValidator, value as string | null)
    }
    if (question.type === 'select' && question.choices) {
      return getSelectChoicesValidator(question, value)
    }
    return t('xlAssessmentWizard.validation.invalidValue')
  }
})

export const getBuildingValidation = (
  buildingQuestion: BuildingQuestion
): Validator<string | number | null | boolean> => {
  switch (buildingQuestion.key) {
    case 'buildingAddress':
    case 'buildingCity':
      return {
        coerce: stringOrNull,
        validate: notEmptyString,
        getErrorReason: (value: string | number | boolean | null) =>
          getMandatoryErrorIfNeeded(buildingQuestion.mandatory, value)
      } as Validator<string | number | null | boolean>
    case 'buildingDescription':
    case 'buildingName':
      return {
        coerce: stringOrNull,
        validate: alwaysValid,
        getErrorReason: () => null
      } as Validator<string | number | null | boolean>
    default:
      return {
        coerce: (value: string | boolean) => stringOrNull(value),
        validate: (value: string | number | boolean | null) =>
          validateDynamicTechnicalData(buildingQuestion)(typeof value === 'string' ? value : null),
        getErrorReason: (value: string | number | boolean | null) =>
          getMandatoryErrorIfNeeded(buildingQuestion.mandatory, value) ||
          getSelectChoicesValidator(buildingQuestion, value)
      }
  }
}

const commonEquipmentValidations: Validations<EditableEquipmentFields> = {
  koneEquipmentNumber: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: alwaysValid,
    getErrorReason: (_value: string | number | null | boolean) => null
  },
  inventoryNumber: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: alwaysValid,
    getErrorReason: (_value: string | number | null | boolean) => null
  },
  manufacturer: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notNull,
    getErrorReason: (value: string | number | null | boolean) => {
      if (value === null || value === '' || value === undefined) {
        return t('xlAssessmentWizard.validation.answerRequired')
      }
      return null
    }
  },
  manufacturingSerialnumber: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: alwaysValid,
    getErrorReason: (_value: string | number | null | boolean) => null
  },
  operationalEnvironment: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notNull,
    getErrorReason: (value: string | number | null | boolean) => {
      if (value === null || value === '' || value === undefined) {
        return t('xlAssessmentWizard.validation.answerRequired')
      }
      return null
    }
  },
  technicalPlatform: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notNull,
    getErrorReason: (value: string | number | null | boolean) => {
      if (value === null || value === '' || value === undefined) {
        return t('xlAssessmentWizard.validation.answerRequired')
      }
      return null
    }
  },
  year: {
    coerce: (value: string | boolean) => numberOrNull(value),
    validate: isIntegerBetween(startYear, endYear),
    getErrorReason: (value: string | number | null | boolean) => {
      if (value === null || value === '' || value === undefined) {
        return t('xlAssessmentWizard.validation.answerRequired')
      }
      if (typeof value === 'number') {
        return getErrorReasonFromValidator(notNullIntBetween(startYear, endYear), value)
      }
      return t('xlAssessmentWizard.validation.invalidValue')
    }
  },
  locationName: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notNull,
    getErrorReason: (value: string | null) => getErrorReasonFromValidator(notNullOrEmptyValidator, value)
  },
  locationAddress: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notEmptyString,
    getErrorReason: (value: string | null) => getErrorReasonFromValidator(notNullOrEmptyValidator, value)
  },
  locationCity: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notEmptyString,
    getErrorReason: (value: string | null) => getErrorReasonFromValidator(notNullOrEmptyValidator, value)
  },
  equipmentAddress: {
    coerce: (value: string | boolean) => stringOrNull(value),
    validate: notEmptyString,
    getErrorReason: (value: string | null) => getErrorReasonFromValidator(notNullOrEmptyValidator, value)
  }
}

const validateDynamicTechnicalData =
  (equipmentQuestion: EquipmentQuestion | BuildingQuestion) => (x: string | number | boolean | null) => {
    const str = typeof x === 'string' ? x : null

    // If mandatory and no value, invalid
    if (equipmentQuestion.mandatory && !str) {
      return false
    }

    // If not select type or no value to validate, valid
    if (equipmentQuestion.type !== 'select' || !Array.isArray(equipmentQuestion.choices) || !str) {
      return true
    }

    // Validate select choices
    const isSingleType = equipmentQuestion.choices.some((ch: string | ChoiceAsKeyValuePair) => typeof ch === 'string')
    return isSingleType
      ? (equipmentQuestion.choices as string[]).includes(str)
      : (equipmentQuestion.choices as ChoiceAsKeyValuePair[]).some(ch => ch.key === str)
  }

const validateMultiChoiceDynamicTechnicalData =
  (equipmentQuestion: EquipmentQuestion, isAPFFromCRM: boolean) => (x: string | number | boolean | null) => {
    const { type, key, choices, mandatory } = equipmentQuestion
    const str = typeof x === 'string' ? x : null

    // If mandatory and no value, invalid
    if (mandatory && !str) {
      return false
    }

    // If not a string value or not select type, valid
    if (!str || type !== 'select' || !Array.isArray(choices)) {
      return true
    }

    // Special handling for APF components from CRM
    if (key === 'apfComponent' && isAPFFromCRM) {
      return true
    }

    // Validate multi-choice selections
    const multiAnswer = str.split(',')
    return (choices as string[]).some(choice => multiAnswer.includes(choice))
  }

export const getElevatorValidation = (
  elevatorQuestion: ElevatorQuestion,
  frontlineEnabledTechnicalPlatforms: string[],
  isAPFFromCRM: boolean
): Validator<string | number | null | boolean> => {
  switch (elevatorQuestion.key) {
    case 'operationalEnvironment':
      return {
        coerce: stringOrNull,
        validate: (value: string | number | boolean | null) => isIncludedIn(operationalEnvironmentOptions, value),
        getErrorReason: (value: string | number | null | boolean) => {
          if (elevatorQuestion.mandatory && isNullOrEmpty(value)) {
            return t('xlAssessmentWizard.validation.answerRequired')
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'additionalDoors':
      return {
        coerce: (value: string | boolean) => numberOrNull(value),
        validate: isIntegerBetween(0, 2),
        getErrorReason: (value: string | number | null | boolean) => {
          if (elevatorQuestion.mandatory && isNullOrEmpty(value)) {
            return t('xlAssessmentWizard.validation.answerRequired')
          }
          if (typeof value === 'number') {
            return getErrorReasonFromValidator(notNullIntBetween(0, 2), value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'equipmentType':
      return {
        coerce: (value: string | boolean) => string(value),
        validate: notNull,
        getErrorReason: (value: string | number | null | boolean) =>
          isNullOrEmpty(value)
            ? t('xlAssessmentWizard.validation.answerRequired')
            : t('xlAssessmentWizard.validation.invalidValue')
      }
    case 'ratedSpeed':
      const speedMin = elevatorQuestion.min ?? 0
      const speedMax = elevatorQuestion.max ?? 20
      return {
        coerce: (value: string | boolean) => numberOrNull(value),
        validate: validateSpeed(elevatorQuestion),
        getErrorReason: (value: string | number | null | boolean) => {
          if (elevatorQuestion.mandatory && isNullOrEmpty(value)) {
            return t('xlAssessmentWizard.validation.answerRequired')
          }
          if (typeof value === 'number') {
            return getErrorReasonFromValidator(
              allValidators(integerValidator, rangeValidator(speedMin, speedMax)),
              value
            )
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'doorsCount':
    case 'ratedLoad':
      const min = elevatorQuestion.min ?? 0
      const max = elevatorQuestion.max ?? 100000
      return {
        coerce: (value: string | boolean) => numberOrNull(value),
        validate: isIntegerBetween(min, max),
        getErrorReason: (value: string | number | null | boolean) => {
          if (elevatorQuestion.mandatory && isNullOrEmpty(value)) {
            return t('xlAssessmentWizard.validation.answerRequired')
          }
          if (typeof value === 'number') {
            return getErrorReasonFromValidator(notNullIntBetween(min, max), value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'technicalPlatform':
      return {
        coerce: (value: string | boolean) => stringOrNull(value),
        validate: (value: string | number | boolean | null) =>
          isIncludedIn(frontlineEnabledTechnicalPlatforms, typeof value === 'string' ? value : null),
        getErrorReason: (value: string | number | null | boolean) => {
          if (elevatorQuestion.mandatory && (value === null || value === '' || value === undefined)) {
            return getErrorReasonFromValidator(notNullOrEmptyValidator, value as string | null)
          }
          if (typeof value === 'string') {
            const validator = elevatorQuestion.mandatory
              ? includedInFromChoiceValidator(frontlineEnabledTechnicalPlatforms)
              : nullOrIncludedInValidator(frontlineEnabledTechnicalPlatforms)
            return getErrorReasonFromValidator(validator as any, value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'controlSystem':
    case 'controllerType':
    case 'technicalId':
    case 'landingDoorType':
    case 'carDoorType':
    case 'driveSystem':
    case 'koneConnectivityDeviceType':
    case 'locationRegion':
    case 'locationCountry':
    case 'manufacturer':
    case 'startsPerYear':
    case 'locationPostalCode':
      return createSelectFieldValidator(
        elevatorQuestion,
        q => value => validateDynamicTechnicalData(q)(typeof value === 'string' ? value : null)
      )

    case 'apfComponent':
      return {
        coerce: (value: string | boolean) => stringOrNull(value),
        validate: (value: string | number | boolean | null) =>
          validateMultiChoiceDynamicTechnicalData(
            elevatorQuestion,
            isAPFFromCRM
          )(typeof value === 'string' ? value : null),
        getErrorReason: (value: string | number | null | boolean) => {
          if (elevatorQuestion.mandatory && isNullOrEmpty(value)) {
            return getErrorReasonFromValidator(notNullOrEmptyValidator, value as string | null)
          }
          if (elevatorQuestion.type === 'select' && elevatorQuestion.choices && !isAPFFromCRM) {
            return getSelectChoicesValidator(elevatorQuestion, value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    default:
      return getCommonEquipmentValidator(elevatorQuestion.key, elevatorQuestion.mandatory)
  }
}

// Helper function to get common equipment validator
const getCommonEquipmentValidator = (key: string, mandatory: boolean): Validator<string | number | null | boolean> => {
  if (!Object.prototype.hasOwnProperty.call(commonEquipmentValidations, key)) {
    return {
      coerce: () => null,
      validate: alwaysValid,
      getErrorReason: () => null
    }
  }

  const validator = commonEquipmentValidations[key as keyof EditableEquipmentFields]
  return {
    coerce: validator.coerce,
    validate: (value: string | number | boolean | null) => {
      if (value === null) {
        return validator.validate(value)
      }
      if (typeof value === 'string') {
        return validator.validate(value as any)
      }
      if (typeof value === 'number') {
        return validator.validate(value as any)
      }
      return false
    },
    getErrorReason: validator.getErrorReason
      ? (value: string | number | null | boolean) => validator.getErrorReason!(value as any)
      : (value: string | number | null | boolean) =>
          mandatory && isNullOrEmpty(value)
            ? t('xlAssessmentWizard.validation.answerRequired')
            : t('xlAssessmentWizard.validation.invalidValue')
  }
}

export const getEscalatorValidation = (
  escalatorQuestion: EscalatorQuestion,
  equipmentType: EquipmentType,
  frontlineEnabledEscalatorTypes: string[]
): Validator<string | number | null> => {
  switch (escalatorQuestion.key) {
    case 'equipmentType':
      return {
        coerce: (value: string | boolean) => string(value),
        validate: notNull,
        getErrorReason: (value: string | number | null) => {
          if (value === null || value === '' || value === undefined) {
            return getErrorReasonFromValidator(notNullOrEmptyValidator, value as string | null)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'numberOfDrives':
    case 'operationMode':
    case 'operationalEnvironmentCondition':
    case 'locationRegion':
    case 'locationCountry':
    case 'locationPostalCode':
    case 'balustradeMaterial':
    case 'escalatorGearbox':
    case 'manufacturer':
    case 'operationalEnvironment':
      return createSelectFieldValidator(
        escalatorQuestion,
        q => value => validateDynamicTechnicalData(q)(typeof value === 'string' ? value : null)
      ) as Validator<string | number | null>
    case 'length':
      const escalatorLengthMin = escalatorQuestion.min ?? 0
      const escalatorLengthMax = escalatorQuestion.max ?? 50000
      if (equipmentType === 'autowalk') {
        const lengthMin = escalatorQuestion.min ?? 0
        const lengthMax = escalatorQuestion.max ?? 100000
        return {
          coerce: (value: string | boolean) => numberOrNull(value),
          validate: isIntegerBetween(lengthMin, lengthMax),
          getErrorReason: (value: string | number | null) => {
            if (escalatorQuestion.mandatory && isNullOrEmpty(value)) {
              return t('xlAssessmentWizard.validation.answerRequired')
            }
            if (typeof value === 'number') {
              return getErrorReasonFromValidator(notNullIntBetween(lengthMin, lengthMax), value)
            }
            return t('xlAssessmentWizard.validation.invalidValue')
          }
        }
      }
      return {
        coerce: (value: string | boolean) => numberOrNull(value),
        validate: (value: string | number | null) =>
          typeof value === 'number' &&
          Number.isInteger(value) &&
          isNumberBetween(escalatorLengthMin, escalatorLengthMax)(value),
        getErrorReason: (value: string | number | null) => {
          if (escalatorQuestion.mandatory && isNullOrEmpty(value)) {
            return t('xlAssessmentWizard.validation.answerRequired')
          }
          if (typeof value === 'number') {
            return getErrorReasonFromValidator(notNullIntBetween(escalatorLengthMin, escalatorLengthMax), value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'rise':
      if (equipmentType === 'escalator') {
        const riseMin = escalatorQuestion.min ?? 0
        const riseMax = escalatorQuestion.max ?? 100000
        return {
          coerce: (value: string | boolean) => numberOrNull(value),
          validate: isIntegerBetween(riseMin, riseMax),
          getErrorReason: (value: string | number | null) => {
            if (escalatorQuestion.mandatory && isNullOrEmpty(value)) {
              return t('xlAssessmentWizard.validation.answerRequired')
            }
            if (typeof value === 'number') {
              return getErrorReasonFromValidator(notNullIntBetween(riseMin, riseMax), value)
            }
            return t('xlAssessmentWizard.validation.invalidValue')
          }
        }
      }
      return {
        coerce: (value: string | boolean) => numberOrNull(value),
        validate: (value: string | number | null) => typeof value === 'number' && Number.isInteger(value),
        getErrorReason: (value: string | number | null) => {
          if (escalatorQuestion.mandatory && isNullOrEmpty(value)) {
            return t('xlAssessmentWizard.validation.answerRequired')
          }
          if (typeof value === 'number') {
            return getErrorReasonFromValidator(integerValidator, value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    case 'escalatorType':
      return {
        coerce: (value: string | boolean) => stringOrNull(value),
        validate: (value: string | number | null) =>
          isIncludedIn(frontlineEnabledEscalatorTypes, typeof value === 'string' ? value : null),
        getErrorReason: (value: string | number | null) => {
          if (escalatorQuestion.mandatory && (value === null || value === '' || value === undefined)) {
            return getErrorReasonFromValidator(notNullOrEmptyValidator, value as string | null)
          }
          if (typeof value === 'string') {
            const validator = escalatorQuestion.mandatory
              ? includedInFromChoiceValidator(frontlineEnabledEscalatorTypes)
              : nullOrIncludedInValidator(frontlineEnabledEscalatorTypes)
            return getErrorReasonFromValidator(validator as any, value)
          }
          return t('xlAssessmentWizard.validation.invalidValue')
        }
      }
    default:
      return getCommonEquipmentValidator(escalatorQuestion.key, escalatorQuestion.mandatory) as Validator<
        string | number | null
      >
  }
}

export type ValidatorFunc<T> = (value: T) => ValidationResult<T>

export const ofPredicate =
  <T>(pred: (x: T) => boolean, reasonTranslationKey: string, translationParams?: object): ValidatorFunc<T> =>
  (x: T) => {
    if (pred(x)) {
      return createValid(x)
    }
    return createInvalid([{ columnId: '', reason: i18n.t(reasonTranslationKey, translationParams as any) as string }])
  }

export const andMapped =
  <A, B>(f: ValidatorFunc<A>, lens: (x: A) => B, g: ValidatorFunc<B>): ValidatorFunc<A> =>
  (x: A) => {
    const fx = f(x)
    if (fx.type === 'valid') {
      const lensedX = lens(x)
      const gx = g(lensedX)
      if (gx.type === 'valid') {
        return createValid(x)
      }
      return gx
    }
    return fx
  }

export const mapErr =
  <A>(mapper: (err: ValidationError) => ValidationError) =>
  (f: ValidatorFunc<A>) =>
  (x: A) => {
    const fx = f(x)
    if (fx.type === 'invalid') {
      return createInvalid(fx.errors.map(mapper))
    }
    return fx
  }

export const column = <A>(columnId: string) => mapErr<A>(err => ({ ...err, columnId }))

export function allValidators<T>(...validators: Array<ValidatorFunc<T>>): ValidatorFunc<T> {
  return (x: T) => {
    const result = validators.map(f => f(x))
    const invalids = result.filter(r => r.type === 'invalid') as Invalid[]

    if (invalids.length > 0) {
      const errors = invalids.flatMap(i => i.errors)
      return createInvalid(errors)
    }

    return createValid(x)
  }
}

// Helper to extract error reason from ValidatorFunc result
const getErrorReasonFromValidator = <T>(validatorFunc: ValidatorFunc<T>, value: T): string | null => {
  const result = validatorFunc(value)
  if (result.type === 'invalid' && result.errors.length > 0) {
    return result.errors.map(error => error.reason).join(', ')
  }
  return null
}

export const notNullValidator = <T>() => ofPredicate<T>(x => x != null, 'xlAssessmentWizard.validation.answerRequired')

export const notNullAnd = <T>(f: ValidatorFunc<T>) => andMapped<T | null, T>(notNullValidator(), n => n!, f)

export const notNullIntBetween = (minInc: number, maxInc: number) =>
  notNullAnd(allValidators(integerValidator, rangeValidator(minInc, maxInc)))

export const notNullOrEmptyValidator = ofPredicate<string | null>(
  x => x != null && x.length > 0 && x.trim().length > 0,
  'xlAssessmentWizard.validation.answerRequired'
)

export const integerValidator = ofPredicate<number>(Number.isInteger, 'xlAssessmentWizard.validation.integerRequired')

export const rangeValidator = (minInc: number, maxInc: number) =>
  ofPredicate<number>(n => n >= minInc && n <= maxInc, 'xlAssessmentWizard.validation.mustBeBetween', {
    minInc,
    maxInc
  })

export const hasLengthValidator = <T extends number>(limit: number) =>
  ofPredicate<T>(value => value.toString().length <= limit, 'xlAssessmentWizard.validation.length', { limit })

export const includedInValidator = <T>(list: ReadonlyArray<T>) =>
  ofPredicate<T>(value => list.includes(value), 'xlAssessmentWizard.validation.answerRequired')

export const includedInFromChoiceValidator = <T>(list: ReadonlyArray<T>) =>
  ofPredicate<T>(value => list.includes(value), 'xlAssessmentWizard.validation.answerMustBeFromChoice')

export const nullOrIncludedInValidator = <T>(list: ReadonlyArray<T>) =>
  ofPredicate<T>(value => !value || list.includes(value), 'xlAssessmentWizard.validation.answerMustBeFromChoice')

export const includedInOrNullValidator = <T>(list: ReadonlyArray<T>) =>
  ofPredicate<T | null>(value => !value || list.includes(value), 'xlAssessmentWizard.validation.answerRequired')

export type ObjectValidator<T> = { [K in keyof T]?: ValidatorFunc<T[K]> | ObjectValidator<T[K]> }

export function constructValidator<T>(
  validator: ObjectValidator<T>,
  keyPath: ReadonlyArray<string | number | keyof T> = []
): ValidatorFunc<T> {
  return (x: T) => {
    const results = Object.keys(validator).map(k => {
      const key = k as keyof T
      const path = [...keyPath, k as string]
      const child = validator[key]

      if (child === undefined) {
        return createValid(x)
      }

      if (child instanceof Function) {
        return column<T[keyof T]>(path.join('.'))(child as any)(x[key])
      }

      const childValidator = constructValidator(child as any, path)
      const childResult = childValidator(x[key] as any)

      if (childResult.type === 'valid') {
        return createValid(x)
      }

      return childResult
    })

    const errors = results.filter(r => r.type === 'invalid').flatMap((i: Invalid) => i.errors)

    if (errors.length > 0) {
      return createInvalid(errors)
    }

    return createValid(x)
  }
}

export const checkMandatoryFields = (questions: EquipmentQuestion[]) =>
  questions.map(mq => ({ [mq.key]: notNullOrEmptyValidator }))

export const checkBuildingMandatoryFields = (questions: BuildingQuestion[]) =>
  questions.map(mq => ({ [mq.key]: notNullOrEmptyValidator }))

export const getListOfKeys = (choices: ChoiceAsKeyValuePair[]) => choices.map(ch => ch.key)

export const checkSelectFields = (questions: EquipmentQuestion[], isLocation: boolean) =>
  isLocation
    ? questions.map(sq => {
        const list = getListOfKeys(sq.choices as ChoiceAsKeyValuePair[]) as string[]
        return { [sq.key]: sq.mandatory ? includedInValidator(list || []) : includedInOrNullValidator(list || []) }
      })
    : questions.map(sq => ({
        [sq.key]: sq.mandatory
          ? includedInValidator((sq.choices as string[]) || [])
          : includedInOrNullValidator((sq.choices as string[]) || [])
      }))

export function getValidationErrors<T>(result: ValidationResult<T>): ValidationError[] {
  if (result.type === 'invalid') {
    return result.errors
  }
  return []
}

// Helper to extract equipment data from row
const getEquipmentFromRow = <T extends Record<string, any>>(row: T): any => {
  // Handle different row structures
  if ('elevator' in row) {
    return row.elevator
  }
  if ('escalator' in row) {
    return row.escalator
  }
  return row
}

export const pairedFieldValidator =
  (pairRelation: (p: any, q: any) => boolean) =>
  (question: EquipmentQuestion) =>
  <T extends Record<string, any>>(row: T): ValidationResult<T> => {
    const equipment = getEquipmentFromRow(row)
    if (question.isPaired) {
      const pairKey = question.pairedWith as string
      const isValid = pairRelation(equipment[pairKey], equipment[question.key])
      if (isValid) {
        return createValid(row)
      }
      return createInvalid([
        createValidationError(
          `${equipment.equipmentCategory}.${question.key}`,
          t('xlAssessmentWizard.validation.eitherOrRequired', {
            either: t(`xlAssessmentWizard.${equipment.equipmentCategory}.${question.key}`),
            or: t(`xlAssessmentWizard.${equipment.equipmentCategory}.${question.pairedWith}`)
          })
        )
      ])
    }
    return createValid(row)
  }
